import { useState } from "react";
import { LinkSimple, ArrowDown, XCircle, ShieldWarning } from "@phosphor-icons/react";

// Valores calculados con el mismo algoritmo que usa el backend
// (SHA-256 sobre JSON canónico + encadenamiento "hash_anterior|seq|hash_contenido"),
// con datos de ejemplo — no corresponden a una empresa real.
const ENTRY_40 = {
  seq: 40,
  accion: "corregir",
  entidad: "registros_jornada",
  usuario_email: "rh@acme.mx",
  motivo: "Corrige omisión de salida reportada por el empleado",
  hash_anterior: "e63b3a9f48819a7a3b51849f3a07b9b108d7dcc619d5ea4906c4300ce778c136",
  hash: "e6992dbbca88ecc3cee825a32337483f0fbbf1dea4e7571fad4f28fdb0db76cc",
};

const ENTRY_41 = {
  seq: 41,
  accion: "decidir",
  entidad: "incidencias",
  usuario_email: "supervisor.norte@acme.mx",
  motivo: "Autoriza incidencia pendiente",
  hash_anterior: ENTRY_40.hash,
  hash: "808a901d91cd0d9e239382a481fbc8c4231fad12b964465f3eb66f2e4cb3dd37",
};

// Si alguien editara directamente en la base de datos el "valor_anterior" del
// evento 40 (por ejemplo, cambiar la hora que quedó registrada antes de la
// corrección), el hash recalculado a partir del nuevo contenido ya no
// coincide con el hash que quedó guardado — este es ese valor recalculado.
const TAMPERED_HASH = "81f4687f85f0e1af37413e2b989b27a52c47896dba4aa12d72c664fb12355211";

function shortHash(h) {
  return `${h.slice(0, 14)}…${h.slice(-8)}`;
}

function EntryCard({ e }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-xs">
      <div className="flex items-center justify-between mb-2">
        <span className="font-display font-bold text-ink">seq {e.seq} · {e.accion}</span>
        <span className="text-slate-500">{e.entidad}</span>
      </div>
      <div className="text-slate-600 mb-2">{e.usuario_email} — {e.motivo}</div>
      <div className="font-mono space-y-1">
        <div><span className="text-slate-400">hash_anterior: </span>{shortHash(e.hash_anterior)}</div>
        <div><span className="text-slate-400">hash: </span><span className="font-bold text-ink">{shortHash(e.hash)}</span></div>
      </div>
    </div>
  );
}

export default function AuditoriaEjemplo() {
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-6 border border-slate-200 rounded-md bg-white overflow-hidden" data-tour="auditoria-ejemplo">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid="auditoria-ejemplo-toggle"
      >
        <span className="flex items-center gap-2 font-display font-bold text-ink text-sm">
          <LinkSimple size={18} weight="duotone" /> Ejemplo: así se garantiza que nadie alteró un registro
        </span>
        <span className="text-xs text-slate-500 font-semibold">{open ? "Ocultar" : "Ver ejemplo"}</span>
      </button>

      {open && (
        <div className="p-5 border-t border-slate-200 space-y-5" data-testid="auditoria-ejemplo-body">
          <p className="text-sm text-slate-600">
            Cada evento de la bitácora (crear, corregir, autorizar…) genera una <strong>huella digital</strong> (hash) calculada
            a partir de su propio contenido <strong>y</strong> de la huella del evento inmediato anterior. Es como los eslabones
            de una cadena: cada uno sostiene al siguiente. Si alguien edita un registro directamente en la base de datos —
            aunque cambie un solo carácter — su huella deja de coincidir con lo que el sistema espera, y el botón
            <strong> "Verificar integridad"</strong> lo detecta al instante, señalando exactamente cuál evento fue alterado.
          </p>

          <div>
            <div className="label-uppercase mb-2">Dos eventos reales y consecutivos de la cadena</div>
            <div className="grid md:grid-cols-2 gap-3 items-start">
              <EntryCard e={ENTRY_40} />
              <EntryCard e={ENTRY_41} />
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs text-emerald-700">
              <ArrowDown size={14} weight="bold" />
              El <span className="font-mono">hash</span> del evento 40 es, carácter por carácter, el mismo valor que el{" "}
              <span className="font-mono">hash_anterior</span> del evento 41 — así quedan enlazados.
            </div>
          </div>

          <div>
            <div className="label-uppercase mb-2 text-red-700">Qué pasa si alguien edita el evento 40 directamente en la base de datos</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-xs">
                <div className="font-bold text-ink mb-1">Hash guardado originalmente</div>
                <div className="font-mono">{shortHash(ENTRY_40.hash)}</div>
              </div>
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-xs">
                <div className="font-bold text-red-800 mb-1">Hash recalculado tras la alteración</div>
                <div className="font-mono text-red-800">{shortHash(TAMPERED_HASH)}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs font-bold text-red-700">
              <XCircle size={16} weight="bold" /> No coinciden — "Verificar integridad" reportaría el evento seq 40 como el primer registro alterado.
            </div>
          </div>

          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            <ShieldWarning size={16} weight="bold" className="mt-0.5 shrink-0" />
            <span>
              Los eventos registrados antes de activar este mecanismo no tienen esta protección — el sistema los cuenta aparte
              como "entradas heredadas sin cadena" y no pueden verificarse criptográficamente.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
